import React from "react";
import { CheckCircledIcon } from "@radix-ui/react-icons";
import { Icon } from '@src/components/icon/Icon';
import { CustomTooltip } from '../tooltip/Tooltip';

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface Option {
  value: string
  label: string
}

export interface ValueRendererProps {
  children?: React.ReactNode
}
const DefaultValueRenderer = ({ children }: ValueRendererProps) => (
  <div className="w-full flex flex-wrap items-center">
    {children}
  </div>
);

export interface ComboboxSingleProps {
  value?: string
  options?: Option[]
  withCustomOptions?: boolean
  ignoredValues?: string[]
  disabled?: boolean
  onValueChange?: (value: string, extraOpts?: Record<string, unknown>) => void,
  Renderer?: (props: ValueRendererProps) => React.ReactNode
}
export function ComboboxSingle({
    value,
    options,
    withCustomOptions = false,
    ignoredValues,
    disabled,
    onValueChange,
    Renderer=DefaultValueRenderer
  }: ComboboxSingleProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);

  const canAddOption =
    search.length > 0
    && !(options ?? []).some((opt) => opt.label.toLowerCase() === search.toLowerCase())
    && withCustomOptions;
  const areCustomOptionsAllowed = withCustomOptions || !options;

  const searchChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }

  const handleAddOption = () => {
    const optionToSelect = options?.find((option) => option.label.toLowerCase().includes(search.toLowerCase()));
    if (options && optionToSelect) {
      onValueChange?.(
        optionToSelect?.value
      );
      setSearch('');
      searchRef.current?.focus();
    } else if (areCustomOptionsAllowed) {
      onValueChange?.(
        search
      );
      setSearch('');
      searchRef.current?.focus();
    }
  };

  const isValueFromOptions = (value: string) => {
    return options?.some(option => option.label === value);
  }

  let availableOptions = options!;

  if (value && !isValueFromOptions(value)) {
    availableOptions = [
      { label: value, value },
      ...availableOptions
    ];
  }

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <Renderer>
          <CustomTooltip content={value ?? 'Select value...'}>
            <PopoverTrigger className="relative flex items-center">
              <Input
                value={value ?? ''}
                className="justify-between px-2 w-full min-w-[210px]"
                placeholder="Select an option..."
                disabled={disabled}
                />
              <Icon type="arrow-bottom" className="absolute right-2"/>
            </PopoverTrigger>
          </CustomTooltip>
        </Renderer>
        <PopoverContent className="w-[200px] p-0">
          <Command
            filter={(value, search) => {
              if (value === "option-add" && search && canAddOption) return 1
              if (value.includes(search)) return 1
              return 0
            }}
          >
            <CommandInput
              ref={searchRef}
              value={search}
              onChangeCapture={searchChangeHandler}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canAddOption) {
                  e.preventDefault();
                  handleAddOption();
                }
              }}
              placeholder="Search option..."
              className="h-9"
              disabled={disabled}
            />
            <CommandGroup>
              {availableOptions.map((option) => {
                const isValueSelected = value === option.value;
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      if (!isValueSelected) {
                        onValueChange?.(option?.value);
                      }
                      searchRef.current?.focus();
                    }}
                    className="flex items-left justify-start relative"
                  >
                    <div className="w-6 min-w-6">
                      {isValueSelected && <CheckCircledIcon />}
                    </div>
                    <div className="max-w-full truncate">
                      {option.label}
                    </div>
                  </CommandItem>
                );
              })}
              {search && !ignoredValues?.includes(search) && areCustomOptionsAllowed ? (
                <CommandItem key="option-add" value="option-add">
                  <Button
                    variant="text"
                    className="w-full flex justify-center"
                    disabled={disabled}
                    onClickCapture={handleAddOption}
                  >
                    <Icon type="plus" /> Add custom option
                  </Button>
                </CommandItem>
              ) : null}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
